import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../hooks/useApi';
import FloorPlanMap from './FloorPlanMap';
import { todayIso } from './bookingsTheme';
import { displayDeskState, whoIsIn, boardCounts, pickLocation } from './displayBoard';

// ─── The plan on the wall ─────────────────────────────────────────────────────
//
// A tablet at the entrance, showing one office's floor plan and who is at which desk
// today. Opened at /?display=floor-plan (optionally &location=<id>) on a device that
// has signed in once and stays signed in. There is no sidebar, no header and nothing
// to tap into: the plan is the page.
//
// It reads the same three answers the Bookings page reads (the desks, today's
// bookings, the seats absent owners have released) and draws them with the same
// FloorPlanMap, so what is on the wall is what people already see on their phones.
// The occupied seat carries the person's initials in their team colour; a reserved
// seat carries its owner's, outlined. The list beside the plan gives the full names,
// because two initials on a tile are only useful to somebody who already knows them.

const REFRESH_MS = 60_000; // who is in changes as people book and arrive

// How the board is fed when a staff member opens it inside the app (?display=floor-plan):
// the same three answers the Bookings page reads. A tablet signed in as a display
// device is given a different loader by FloorPlanDisplayShell, against /api/display.
export async function loadBoardAsStaff(today) {
  const [locations, bookings, avail] = await Promise.all([
    api.get('desk-locations').catch(() => null),
    api.get('bookings', { from: today, to: today, resource_type: 'desk' }).catch(() => null),
    api.get('desk-availability', { from: today, to: today }).catch(() => null),
  ]);
  if (locations === null && bookings === null && avail === null) throw new Error('Could not reach the server');
  return { locations, bookings, released: avail ? (avail.released || {}) : null };
}

const parseZones = (loc) => { try { return JSON.parse(loc?.layout_json || '[]'); } catch { return []; } };

const clockText = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const dateText = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

const initialsOf = (full) => String(full || '').trim().split(/\s+/)
  .filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();

export default function FloorPlanDisplayPage({ locationId = null, loadBoard = loadBoardAsStaff, onSignOut = null }) {
  const [now, setNow] = useState(() => new Date());
  const [locations, setLocations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [released, setReleased] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  // The board runs for days. The date it asks about has to roll over at midnight, so
  // it is derived from the ticking clock rather than read once on load.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const today = useMemo(() => todayIso(), [now.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps

  // One load does the lot. A failed refresh keeps the last board on the wall rather
  // than blanking it, and says so in the footer.
  const refresh = useCallback(async () => {
    let next;
    try { next = await loadBoard(today); }
    catch (e) { setError(e?.message || 'Could not reach the server'); return; }
    setError(null);
    if (next.locations) setLocations(next.locations);
    if (next.bookings) setBookings(next.bookings);
    if (next.released) setReleased(next.released);
    setLastRefresh(new Date());
  }, [loadBoard, today]);

  useEffect(() => {
    (async () => { await refresh(); setLoaded(true); })();
    const t = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const location = useMemo(() => pickLocation(locations, locationId), [locations, locationId]);
  const desks = location?.desks || [];
  const getState = useCallback((desk) => displayDeskState(desk, { bookings, released, date: today }), [bookings, released, today]);
  const people = useMemo(() => whoIsIn(desks, bookings, today), [desks, bookings, today]);
  const counts = useMemo(() => boardCounts(desks, bookings, released, today), [desks, bookings, released, today]);

  const heightRatio = location?.name === 'PP' ? 0.64 : 0.55;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Brand band. Navy in both modes, like the sidebar. */}
      <header className="flex items-center justify-between px-6 py-3 text-white" style={{ background: 'var(--navy, #0D1D32)' }}>
        <div className="flex items-center gap-4">
          <img src="/av-logo-white.png" alt="Alton Valley" style={{ height: 34, width: 'auto', objectFit: 'contain' }} />
          <div>
            <div className="text-[18px] font-bold leading-tight">{location ? `${location.name} · Who's in today` : 'Who\'s in today'}</div>
            <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.65)' }}>{dateText(now)}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[22px] font-bold tabular-nums leading-tight" style={{ color: '#E5B87A' }}>{counts.in}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.65)' }}>in today</div>
          </div>
          <div className="text-right">
            <div className="text-[22px] font-bold tabular-nums leading-tight">{counts.free}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.65)' }}>desks free</div>
          </div>
          <div className="text-[30px] font-bold tabular-nums pl-4" style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
            {clockText(now)}
          </div>
        </div>
      </header>

      <main className="flex-1 flex gap-6 p-6 min-h-0">
        {/* The plan, sized to the height left under the header so the whole office is on
            screen without scrolling. FloorPlanMap scales to its width, so the width is
            what is capped: (viewport minus chrome) divided by the plan's aspect ratio. */}
        <section className="flex-1 min-w-0 flex items-start justify-center">
          {!loaded ? (
            <div className="text-[14px] text-gray-500 py-20">Loading the plan…</div>
          ) : !location ? (
            <div className="text-[14px] text-gray-500 py-20">No office has a floor plan to show.</div>
          ) : (
            <div className="w-full" style={{ maxWidth: `calc((100vh - 170px) / ${heightRatio})` }}>
              <FloorPlanMap
                fill
                title={location.name}
                desks={desks}
                getState={getState}
                zones={parseZones(location)}
                heightRatio={heightRatio}
              />
            </div>
          )}
        </section>

        {/* Names, because initials are not enough at the door. */}
        <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-baseline justify-between">
            <span className="text-[13px] font-bold text-gray-900">In today</span>
            <span className="text-[11px] text-gray-500">{people.length} {people.length === 1 ? 'person' : 'people'}</span>
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {people.length === 0 && loaded && (
              <li className="px-4 py-6 text-[12px] text-gray-500 text-center">Nobody has booked a desk yet</li>
            )}
            {people.map(p => (
              <li key={`${p.desk_id}-${p.person}-${p.slot}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                  style={{ background: p.color || '#5b6bb5' }}>
                  {initialsOf(p.person)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-semibold text-gray-900 truncate">{p.person}</span>
                  {p.host && <span className="block text-[10px] text-gray-500 truncate">Guest of {p.host}</span>}
                </span>
                <span className="text-[12px] font-bold text-gray-700 tabular-nums flex-shrink-0">
                  {p.desk_label}{p.slot !== 'full' ? <span className="text-[10px] font-semibold text-gray-500"> · {p.slot.toUpperCase()}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </aside>
      </main>

      <footer className="px-6 py-2 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-200 bg-white">
        <span>{error ? <span className="text-red-600 font-semibold">{error} · showing the last plan received</span> : 'Refreshes every minute'}</span>
        <span className="flex items-center gap-3">
          {lastRefresh ? `Updated ${clockText(lastRefresh)}` : ''}
          {/* Quiet on purpose: a wall display should not invite anyone to sign it out, but
              whoever set it up needs a way to, without an administrator's help. */}
          {onSignOut && (
            <button type="button" onClick={onSignOut} className="text-gray-400 hover:text-gray-600 underline">
              Sign out this display
            </button>
          )}
        </span>
      </footer>
    </div>
  );
}
